import type { ReactNode } from 'react';

/**
 * Render a coach name with the "AI" particle highlighted via the
 * coach's accent color.
 *
 * Original Phase 2 design used uppercase "AI" inside coach names
 * (AIro / GAIr / VAIra / OnAI / KAIru) but the visual treatment
 * read awkwardly when "AI" sat in the middle of the name. First
 * polish pass tried italic + colored "ai" — italic added a layer of
 * confusion. Final treatment: color-only highlight, with the first
 * letter capitalized when "AI" starts the name (so the name reads
 * as a proper noun beginning with a capital).
 *
 * The fixture now spells the names in proper-noun case (Part 6 ruling,
 * 2026-10-12), and the search is case-insensitive so the highlight lands
 * on the same letters as before:
 *
 *   Airo  → colored "Ai" + "ro"   ("Airo")
 *   Gair  → "G" + colored "ai" + "r"   ("Gair")
 *   Vaira → "V" + colored "ai" + "ra"  ("Vaira")
 *   Onai  → "On" + colored "ai"        ("Onai")
 *   Kairu → "K" + colored "ai" + "ru"  ("Kairu")
 *
 * Uses the first occurrence of "ai" in any case. If none is found,
 * returns the name unchanged — defensive fallback.
 */
export function renderCoachName(name: string, accentColor: string): ReactNode {
  const idx = name.toLowerCase().indexOf('ai');
  if (idx === -1) return name;
  const before = name.slice(0, idx);
  const after = name.slice(idx + 2);
  // Capital "Ai" when starting a name; lowercase "ai" mid-/end-name.
  const ai = idx === 0 ? 'Ai' : 'ai';
  return (
    <>
      {before}
      <span style={{ color: accentColor }}>{ai}</span>
      {after}
    </>
  );
}
